import { Icon, IconsEnum } from 'components/Icons';
import { PathName, getRouteByKey } from 'utility/routes';
import Persist, { StorageKind } from 'lib/persist';
import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { RootState, useAppDispatch } from 'lib/centralStore';
import { SubmitErrorHandler, SubmitHandler, useForm } from 'react-hook-form';
import { setContacts, setEmailSessionID, setTelephoneSessionID } from 'features/signup/signup';
import { useLazySendOTPQuery, useLazyVerifyOTPQuery } from 'features/api/userSlice';

import { Button } from 'components/button';
import { ButtonRegistrationWrapper } from '../buttonRegistrationWrapper';
import { REGISTRATION_STEP_KEY } from 'features/signup/constants';
import { RichText } from 'components/richText';
import { TextInput } from 'components/inputs/textInput';
import contactStyle from './contacts.module.scss';
import { gtag } from 'utility/functions';
import styles from 'components/page-content/registrati/registrati.module.scss';
import useCountdown from 'hooks/useCountDown';
import { useGetLabelByKey } from 'hooks/useLingUI';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { SelectInput, SelectOptionType } from 'components/inputs/selectInput';
import { CSVCountryCodesType, useGeoInfo } from 'features/signup/signUpHooks';

const localStorage = Persist(StorageKind.local);

export type ContactsLabel = {
  titleContacts: string;
  descriptionContacts: string;
  email: string;
  placeholderEmail: string;
  optEmail: string;
  nextButton: string;
  telephone: string;
  placeholderTelephone: string;
  optTelephone: string;
  sendAgainLabel: string;
  labelWaitTimeNewOtp: string;
};

export type ContactsProps = {
  next: Dispatch<SetStateAction<boolean>>;
  back?: Dispatch<SetStateAction<boolean>>;
  labels: ContactsLabel;
};

export const Contacts: React.FC<ContactsProps> = ({ next, back, labels }) => {
  const state = useSelector((store: RootState) => store.signup);
  const [isEmailOTPVisible, setIsEmailOTPVisible] = useState(false);
  const [isTelephoneOTPVisible, setIsTelephoneOTPVisible] = useState(false);
  const [proceed, setProceed] = useState(false);
  const labelIndietro = useGetLabelByKey('indietro')!;
  const dispatch = useAppDispatch();
  const [trigger, { error }] = useLazySendOTPQuery();
  const [validationOTPTrigger] = useLazyVerifyOTPQuery();

  const { getCountryCodes } = useGeoInfo();

  const DEFAULT_COUNTRY_CODE = {
    value: '39',
    label: 'IT (+39)',
  };
  const DEFAULT_COUNTRY_CODE_SLECTION: Array<SelectOptionType> = [DEFAULT_COUNTRY_CODE];
  const [countryCodesCsv, setcountryCodesCsv] = useState<CSVCountryCodesType>();
  const [countryCodes, setCountryCodes] = useState<Array<SelectOptionType>>(DEFAULT_COUNTRY_CODE_SLECTION);

  useEffect(() => {
    getCountryCodes().then((r) => setcountryCodesCsv(r));
  }, [getCountryCodes]);

  useEffect(() => {
    if (countryCodesCsv && countryCodesCsv.length > 0) {
      let transformedCountryCodes = countryCodesCsv.map((country) => ({
        value: country.internationalPrefix,
        label: `${country.sigla} (+${country.internationalPrefix})`,
      }));

      const uniqueValues = Array.from(new Set(transformedCountryCodes.map((a) => a.value)));
      transformedCountryCodes = uniqueValues
        .map((value) => transformedCountryCodes.find((a) => a.value === value))
        .filter((item): item is { value: string; label: string } => item !== undefined);
      transformedCountryCodes.sort((a, b) => a.label.localeCompare(b.label));

      setCountryCodes([...DEFAULT_COUNTRY_CODE_SLECTION, ...transformedCountryCodes]);
    }
  }, [countryCodesCsv]);

  const { asPath } = useRouter();
  const registrationUrl = useMemo(() => getRouteByKey(PathName.registrationUrl), []);

  const telephoneRegex = /^[1-9]\d*$/;

  const time = useCountdown({
    minutes: 1,
    format: 'mm:ss',
  });

  const emailInfoSchema = z.object({
    email: z.string().email(),
    otp: z.string().min(4),
  });
  const telephoneInformation = z.object({
    countryCode: z.string().min(1),
    telephone: z.string().regex(telephoneRegex),
    otp: z.string().min(4),
  });
  type EmailValidation = z.infer<typeof emailInfoSchema>;
  type TelephoneInformationValidation = z.infer<typeof telephoneInformation>;

  const { control, handleSubmit, setError, clearErrors, getValues, setValue, getFieldState, formState } =
    useForm<EmailValidation>({
      resolver: zodResolver(emailInfoSchema),
      mode: 'onChange',
      reValidateMode: 'onChange',
      defaultValues: {
        email: state.email,
      },
    });

  const {
    control: secondControl,
    handleSubmit: handleSecondSubmit,
    getValues: getSecondValues,
    clearErrors: clearSecondFormErrors,
    setError: setSecondFormError,
    getFieldState: getSecondFormFieldState,
    formState: secondFormState,
  } = useForm<TelephoneInformationValidation>({
    resolver: zodResolver(telephoneInformation),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      countryCode: DEFAULT_COUNTRY_CODE.value,
      telephone: state.telephone,
    },
  });

  useEffect(() => {
    const err = error as { data: { response: { descrizione: string } } };
    const secondTypeOfError = error as { data: { descrizione: string } };
    if (!!err && err.data && err.data.response) {
      setError('email', { message: err.data.response.descrizione });
    } else if (!!secondTypeOfError && secondTypeOfError?.data?.descrizione) {
      setError('email', { message: secondTypeOfError.data.descrizione });
    }
  }, [error, setError]);

  useEffect(() => {
    const err = error as { data?: { response?: { descrizione?: string } } };
    const secondTypeOfError = error as { data: { descrizione: string } };
    if (!!err && err.data && err.data.response && proceed) {
      setSecondFormError('telephone', { message: err.data.response.descrizione });
    } else if (!!secondTypeOfError && secondTypeOfError?.data?.descrizione) {
      setSecondFormError('telephone', { message: secondTypeOfError.data.descrizione });
    }
  }, [error, setSecondFormError, proceed]);

  useEffect(() => {
    if (asPath.includes(registrationUrl)) {
      const step = localStorage.getItem(REGISTRATION_STEP_KEY);

      if (proceed) {
        if (step !== 'contacts-telephone') {
          localStorage.setItem(REGISTRATION_STEP_KEY, 'contacts-telephone');
          gtag(
            {
              section: 'RegistrationStepContactsTelephone',
              detail: 'RegistrationStepContactsTelephone',
              event: 'RegistrationStepContactsTelephone',
            },
            true
          );
        }
      } else if (step !== 'contacts-email') {
        localStorage.setItem(REGISTRATION_STEP_KEY, 'contacts-email');
        gtag(
          {
            section: 'RegistrationStepContactsEmail',
            detail: 'RegistrationStepContactsEmail',
            event: 'RegistrationStepContactsEmail',
          },
          true
        );
      }
    }
  }, [registrationUrl, asPath, proceed]);

  const requestEmailOTP = async () => {
    try {
      time.reset();
      clearErrors('otp');
      clearErrors('email');
      setError('root', { types: { custom: 'loading' } });
      const { userId } = await trigger({
        type: 'email',
        contact: getValues('email'),
      }).unwrap();
      setIsEmailOTPVisible(true);
      dispatch(setEmailSessionID(userId));
      clearErrors('root');
    } catch (error) {}
  };

  const requestTelephoneOTP = async () => {
    try {
      time.reset();
      clearSecondFormErrors('otp');
      clearSecondFormErrors('telephone');
      clearSecondFormErrors('countryCode');
      setSecondFormError('root', { types: { custom: 'loading' } });
      const { userId } = await trigger({
        type: 'tel',
        contact: `+${getSecondValues('countryCode')}${getSecondValues('telephone')}`,
      }).unwrap();
      setIsTelephoneOTPVisible(true);
      dispatch(setTelephoneSessionID(userId));
      clearSecondFormErrors('root');
    } catch (error) {}
  };

  const firstStepSuccessHandler: SubmitHandler<EmailValidation> = async ({ email, otp }) => {
    setError('root', { types: { custom: 'loading' } });
    const { message } = await validationOTPTrigger({
      type: 'email',
      contact: email,
      otp,
      id: state.userSessionEmail!,
    }).unwrap();
    clearErrors('root');
    if (message) {
      setError('otp', { types: { custom: message } });
    } else {
      setValue('otp', '');
      clearErrors('otp');
      setProceed(true);
    }
  };

  const handleEmailOTP: SubmitErrorHandler<EmailValidation> = async (data) => {
    if (getValues('email') && !isEmailOTPVisible) {
      requestEmailOTP();
    }
  };

  const handleNumberOTP: SubmitErrorHandler<TelephoneInformationValidation> = async () => {
    if (!secondFormState.errors.telephone && !isTelephoneOTPVisible) {
      requestTelephoneOTP();
    }
  };

  const submitWholeForm: SubmitHandler<TelephoneInformationValidation> = async ({ countryCode, telephone, otp }) => {
    if (Object.keys(secondFormState.errors).length === 0) {
      setSecondFormError('root', { types: { custom: 'loading' } });
      const fullPhoneNumber = `+${countryCode}${telephone}`;
      const { message } = await validationOTPTrigger({
        type: 'tel',
        contact: fullPhoneNumber,
        otp,
        id: state.userSessionTelephone!,
      }).unwrap();
      clearSecondFormErrors('root');
      if (message) {
        setSecondFormError('otp', { types: { custom: message } });
      } else {
        clearSecondFormErrors('root');
        dispatch(setContacts({ email: getValues('email'), telephone: telephone, countryCode }));
        next(true);
      }
    }
  };

  const isDisabledTelephone = useMemo(() => {
    return isTelephoneOTPVisible ? !!Object.keys(secondFormState.errors).length : !!secondFormState.errors['telephone'];
  }, [isTelephoneOTPVisible, secondFormState]);

  const isDisabledEmail = useMemo(() => {
    return isEmailOTPVisible ? !!Object.keys(formState.errors).length : !!formState.errors['email'];
  }, [isEmailOTPVisible, formState]);

  const VerifyButtonSection = ({ onClick }: { onClick: () => void }) => {
    const formattedLabelTimeNewOtp = labels.labelWaitTimeNewOtp.replace('{{TIME}}', time.formatted);
    return !time.isRunning ? (
      <div className={contactStyle.containerNewCode}>
        <Button className={contactStyle.buttonNewCode} visualizationType="secondary" size="small" onClick={onClick}>
          <span className={contactStyle.newCode}>{labels.sendAgainLabel}</span>
        </Button>
      </div>
    ) : (
      <button className={contactStyle.buttonCode} type="button" onClick={onClick} disabled={time.isRunning}>
        <span className={contactStyle.wait}>{formattedLabelTimeNewOtp}</span>
      </button>
    );
  };

  return (
    <div className={styles.formContainer}>
      <div className={styles.title}>{labels.titleContacts}</div>
      <RichText containerClassName={styles.subTitle}>{labels.descriptionContacts}</RichText>
      {!proceed ? (
        <form className={styles.form} onSubmit={handleSubmit(firstStepSuccessHandler, handleEmailOTP)}>
          <TextInput
            label={labels.email}
            placeholder={labels.placeholderEmail}
            control={control}
            name="email"
            hasMarginBottom
          />
          {isEmailOTPVisible && (
            <React.Fragment>
              <TextInput label={labels.optEmail} control={control} name="otp" />
              <VerifyButtonSection onClick={requestEmailOTP} />
            </React.Fragment>
          )}
          <div className={styles.buttonsContainer}>
            <button type="button" className={styles.backButton} onClick={() => back!(true)}>
              <Icon iconId={IconsEnum.ARROW} className={styles.icon} color="var(--color-on-bg-primary)" />
              <span className={styles.btnBackLabel}>{labelIndietro}</span>
            </button>
            <button className={styles.nextButton} type="submit" disabled={isDisabledEmail}>
              {isEmailOTPVisible ? labels.nextButton : 'invia codice'}
            </button>
          </div>
        </form>
      ) : (
        <form onSubmit={handleSecondSubmit(submitWholeForm, handleNumberOTP)} className={styles.form}>
          <div className={styles.telContainer}>
            <SelectInput
              control={secondControl}
              name="countryCode"
              label={'Prefisso'} // TODO mettere label da cms
              options={countryCodes}
              hasMarginBottom
              className={styles.countryCode}
            />
            <TextInput
              label={labels.telephone}
              placeholder={labels.placeholderTelephone}
              control={secondControl}
              name="telephone"
              type="tel"
              hasMarginBottom
            />
          </div>
          {isTelephoneOTPVisible && (
            <React.Fragment>
              <TextInput label={labels.optTelephone} control={secondControl} name="otp" />
              <VerifyButtonSection onClick={requestTelephoneOTP} />
            </React.Fragment>
          )}
          <ButtonRegistrationWrapper
            handleGoBack={() => {
              setIsEmailOTPVisible(false);
              setIsTelephoneOTPVisible(false);
              clearErrors('email');
              setProceed(false);
            }}
            isNextDisabled={isDisabledTelephone}
          ></ButtonRegistrationWrapper>
        </form>
      )}
    </div>
  );
};
